"use client";

import { useEffect, useState } from "react";
import { Play, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CategoryHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const targetDate = new Date();
  targetDate.setDate(targetDate.getDate() + 14);
  targetDate.setHours(targetDate.getHours() + 6);
  targetDate.setMinutes(targetDate.getMinutes() + 22);
  targetDate.setSeconds(targetDate.getSeconds() + 47);
  const [timeLeft, setTimeLeft] = useState({
    days: 14,
    hours: 6,
    minutes: 22,
    seconds: 47
  });
  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date().getTime();
      const distance = targetDate.getTime() - now;
      if (distance < 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
        return;
      }
      setTimeLeft({
        days: Math.floor(distance / (1000 * 60 * 60 * 24)),
        hours: Math.floor(distance % (1000 * 60 * 60 * 24) / (1000 * 60 * 60)),
        minutes: Math.floor(distance % (1000 * 60 * 60) / (1000 * 60)),
        seconds: Math.floor(distance % (1000 * 60) / 1000)
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const tickerItems = ["ACERO ESTRUCTURAL", "SOLUCIONES INDUSTRIALES", "KALISCH ACERO", "CALIDAD CERTIFICADA", "ACERO ESTRUCTURAL", "SOLUCIONES INDUSTRIALES", "KALISCH ACERO", "CALIDAD CERTIFICADA"];
  const burstPoints = Array.from({
    length: 24
  }, (_, i) => {
    const angle = i * Math.PI * 2 / 24;
    const radius = i % 2 === 0 ? 50 : 38;
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    return `${x}% ${y}%`;
  }).join(", ");
  const burstClipPath = `polygon(${burstPoints})`;
  const smallBurstPoints = Array.from({
    length: 24
  }, (_, i) => {
    const angle = i * Math.PI * 2 / 24;
    const radius = i % 2 === 0 ? 50 : 36;
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    return `${x}% ${y}%`;
  }).join(", ");
  const smallBurstClipPath = `polygon(${smallBurstPoints})`;
  return <section data-section-id="1467" id="category-hero" className="bg-background text-foreground relative overflow-hidden pt-24 md:pt-32 pb-0">
      {}
      <div className="absolute top-0 right-0 w-full md:w-2/3 h-full opacity-[0.15] pointer-events-none" style={{
      backgroundImage: `radial-gradient(circle, var(--primary) 2px, transparent 2px)`,
      backgroundSize: "16px 16px"
    }} aria-hidden="true" />

      <div ref={ref} className={`relative container mx-auto px-6 md:px-12 max-w-[1440px] transition-all duration-500 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"}`}>
        {}
        <div className="mb-12 md:mb-16">
          <span className="inline-block font-mono text-xs md:text-sm tracking-[0.18em] uppercase text-primary" style={{
          fontWeight: 600
        }}>
            DIVISIÓN 01 / ACERO{" "}
            <span className="ml-2 inline-flex items-center gap-1 align-middle">
              <span className="inline-block w-2.5 h-2.5 bg-primary" />
              <span className="inline-block w-2.5 h-2.5 border-2 border-primary" />
              <span className="inline-block w-2.5 h-2.5 border-2 border-primary" />
              <span className="inline-block w-2.5 h-2.5 border-2 border-primary" />
              <span className="inline-block w-2.5 h-2.5 border-2 border-primary" />
            </span>
          </span>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start pb-32 md:pb-44">
          {}
          <div className="lg:col-span-8 relative">
            {}
            <h1 className="absolute inset-0 font-default uppercase pointer-events-none select-none translate-x-2 translate-y-2" style={{
            fontSize: "clamp(3.25rem, 8vw, 7.5rem)",
            fontWeight: 900,
            letterSpacing: "-0.04em",
            lineHeight: 0.88,
            color: "var(--secondary-100)"
          }} aria-hidden="true">
              FORJAMOS EL{" "}
              <span className="inline-block">FUTURO</span>{" "}
              DE LA CONSTRUCCIÓN
            </h1>

            <h1 className="relative font-default uppercase text-foreground" style={{
            fontSize: "clamp(3.25rem, 8vw, 7.5rem)",
            fontWeight: 900,
            letterSpacing: "-0.04em",
            lineHeight: 0.88
          }}>
              FORJAMOS EL{" "}
              <em className="not-italic inline-block bg-primary text-primary-foreground border-[3px] border-foreground px-2 py-0.5 -rotate-2" style={{
              transform: "rotate(-2deg)"
            }}>
                FUTURO
              </em>{" "}
              DE LA CONSTRUCCIÓN
            </h1>

            {}
            <p className="relative mt-10 md:mt-12 max-w-2xl text-muted-foreground" style={{
            fontFamily: "var(--font-sans)",
            fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)",
            fontWeight: 400,
            letterSpacing: "-0.005em",
            lineHeight: 1.55
          }}>
              Líderes en la industria del acero, ofreciendo soluciones estructurales de alta resistencia y procesos sostenibles para los proyectos más ambiciosos de México.
            </p>

            {}
            <div className="relative mt-10 md:mt-12 flex flex-col sm:flex-row gap-6">
              <Link to="/CatalogPage">
                <Button className="font-default uppercase bg-primary text-primary-foreground border-[3px] border-foreground hover:bg-primary-600 hover:translate-x-1 hover:translate-y-1 transition-all duration-150 px-7 py-6 text-base tracking-[0.06em] rounded-md" style={{
                boxShadow: "6px 6px 0 var(--foreground)",
                fontWeight: 700
              }} onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "2px 2px 0 var(--foreground)";
              }} onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 var(--foreground)";
              }}>
                  VER CATÁLOGO
                  <ArrowRight className="ml-2 h-5 w-5" strokeWidth={2.5} />
                </Button>
              </Link>

              <Link to="/AboutUsPage">
                <Button variant="secondary" className="font-default uppercase bg-background text-foreground border-[3px] border-foreground hover:bg-muted hover:translate-x-1 hover:translate-y-1 transition-all duration-150 px-7 py-6 text-base tracking-[0.06em] rounded-md" style={{
                boxShadow: "6px 6px 0 var(--secondary)",
                fontWeight: 700
              }} onMouseEnter={e => {
                e.currentTarget.style.boxShadow = "2px 2px 0 var(--secondary)";
              }} onMouseLeave={e => {
                e.currentTarget.style.boxShadow = "6px 6px 0 var(--secondary)";
              }}>
                  <Play className="mr-1 h-5 w-5" strokeWidth={2.5} />
                  NUESTRA HISTORIA
                </Button>
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-4 relative flex flex-col items-center lg:items-end justify-start gap-8 lg:gap-4 mt-8 lg:mt-0">
            {}
            <div className="relative w-[280px] h-[280px] md:w-[300px] md:h-[300px]" style={{
            transform: "rotate(6deg)"
          }}>
              {}
              <div className="absolute inset-0 bg-foreground" style={{
              clipPath: burstClipPath
            }} aria-hidden="true" />
              {}
              <div className="absolute inset-[6px] bg-accent flex flex-col items-center justify-center text-center px-6" style={{
              clipPath: burstClipPath
            }}>
                <span className="font-mono uppercase text-accent-foreground mb-2" style={{
                fontSize: "0.75rem",
                fontWeight: 600,
                letterSpacing: "0.18em"
              }}>
                  PRÓXIMA ENTREGA
                </span>
                <div className="flex items-baseline gap-1.5 text-accent-foreground font-default" style={{
                fontWeight: 700
              }}>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-4xl leading-none tabular-nums">
                      {String(timeLeft.days).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[0.6rem] tracking-[0.18em] mt-1" style={{
                    fontWeight: 600
                  }}>
                      D
                    </span>
                  </div>
                  <span className="text-2xl md:text-3xl leading-none">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-4xl leading-none tabular-nums">
                      {String(timeLeft.hours).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[0.6rem] tracking-[0.18em] mt-1" style={{
                    fontWeight: 600
                  }}>
                      H
                    </span>
                  </div>
                  <span className="text-2xl md:text-3xl leading-none">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-4xl leading-none tabular-nums">
                      {String(timeLeft.minutes).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[0.6rem] tracking-[0.18em] mt-1" style={{
                    fontWeight: 600
                  }}>
                      M
                    </span>
                  </div>
                  <span className="text-2xl md:text-3xl leading-none">:</span>
                  <div className="flex flex-col items-center">
                    <span className="text-3xl md:text-4xl leading-none tabular-nums">
                      {String(timeLeft.seconds).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-[0.6rem] tracking-[0.18em] mt-1" style={{
                    fontWeight: 600
                  }}>
                      S
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {}
            <div className="relative w-[150px] h-[150px] md:w-[170px] md:h-[170px] md:-mt-4 md:-ml-12" style={{
            transform: "rotate(-10deg)"
          }}>
              <div className="absolute inset-0 bg-foreground" style={{
              clipPath: smallBurstClipPath
            }} aria-hidden="true" />
              <div className="absolute inset-[4px] bg-secondary flex items-center justify-center text-center" style={{
              clipPath: smallBurstClipPath
            }}>
                <span className="font-default uppercase text-secondary-foreground" style={{
                fontSize: "1.5rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                lineHeight: 0.95
              }}>
                  ACERO
                  <br />
                  PURO
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="relative w-full border-t-[3px] border-b-[3px] border-foreground bg-primary overflow-hidden">
        <div className="flex whitespace-nowrap py-2 animate-[ticker_30s_linear_infinite]">
          {[...tickerItems, ...tickerItems].map((item, i) => <span key={i} data-index={i} className="font-mono uppercase text-primary-foreground px-6 flex items-center gap-6" style={{
          fontSize: "0.875rem",
          fontWeight: 600,
          letterSpacing: "0.18em"
        }}>
              {item}
              <span className="inline-block w-2 h-2 bg-primary-foreground" />
            </span>)}
        </div>
      </div>

      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
      `}</style>
    </section>;
}